import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { Checkbox } from "primereact/checkbox";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { Steps } from "primereact/steps";
import {
  GetClientDto,
  ClientAddressDto,
  ClientDocumentDto,
} from "../../types/client.types";
import {
  getClientById,
  createClient,
  updateClient,
} from "../../service/client/ClientServices";
import { Toast } from "primereact/toast";
import { getAllCountries } from "../../service/countries/CountryServices";
import { getAllIndustries } from "../../service/industry/IndustryServices";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrash,
  faCheck,
  faXmark,
  faFile,
  faDownload,
  faUser,
  faMapMarkerAlt,
  faAddressBook,
  faCog,
  faFileAlt,
  faArrowRight,
  faArrowLeft
} from "@fortawesome/free-solid-svg-icons";
import { FileUpload } from "primereact/fileupload";
import { getAllBusinessTypes } from "../../service/businessType/BusinessTypeService";
import { getAllPaymentTerms } from "../../service/paymentTerms/PaymentTermsService";
import {
  MAX_FILE_SIZE,
  ALLOWED_EXTENSIONS,
  ALLOWED_FILE_TYPES,
} from "../../shared/constant/FileValidation";
import { showToast } from "../../store/slices/toastSlice";
import { InvoiceDocumentDto } from "../../types/invoice.types";
import { setError } from "../../store/slices/errorSlice";

interface ClientFormProps {
  mode?: "create" | "edit";
}

const ClientForm: React.FC<ClientFormProps> = ({ mode = "create" }) => {
  const { id } = useParams<{ id: string }>();
  const localization = useSelector((state: any) => state.localization);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [client, setClient] = useState<GetClientDto>({
    client_id: 0,
    client_name: "",
    primary_contact_person: "",
    website_url: "",
    email: "",
    phone: "",
    tax_exempt: false,
    notes: "",
    payment_term_id: 0,
    business_type_id: 0,
    billing_address: {
      address_line1: "",
      address_line2: "",
      city: "",
      state: "",
      postal_code: "",
      country_id: undefined,
    },
    shipping_address: {
      address_line1: "",
      address_line2: "",
      city: "",
      state: "",
      postal_code: "",
      country_id: undefined,
    },
    currency_code: "",
    pan_no: "",
    contact_persons: [],
    documents: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  });

  const [countries, setCountries] = useState<{ label: string; value: number }[]>([]);
  const [industries, setIndustries] = useState<{ label: string; value: string }[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeIndex, setActiveIndex] = useState(0);
  const [sameAsBilling, setSameAsBilling] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useRef<Toast>(null);
  const fileUploadRef = useRef<any>(null);
  const [fileUploadError, setFileUploadError] = useState<string>("");
  const [paymentTerms, setPaymentTerms] = useState<{ label: string; value: number }[]>([]);
  const [businessTypes, setBusinessTypes] = useState<{ label: string; value: number, description: string }[]>([]);
  const [industryTypes, setIndustryTypes] = useState<{ label: string; value: number }[]>([]);
  const error = useSelector((state: any) => state.error.errorMessage);

  // Define steps for the wizard
  const steps = [
    {
      label: localization?.GeneralInformation || "General Information",
      command: () => setActiveIndex(0),
      template: (item: any, options: any) => {
        return (
          <span className={options.className} onClick={options.onClick} style={{ cursor: 'pointer' }}>
            <span className="p-steps-number">
              <FontAwesomeIcon icon={faUser} />
            </span>
            <span className="p-steps-title">{item.label}</span>
          </span>
        );
      }
    },
    {
      label: localization?.Addresses || "Addresses",
      command: () => setActiveIndex(1),
      template: (item: any, options: any) => {
        return (
          <span className={options.className} onClick={options.onClick} style={{ cursor: 'pointer' }}>
            <span className="p-steps-number">
              <FontAwesomeIcon icon={faMapMarkerAlt} />
            </span>
            <span className="p-steps-title">{item.label}</span>
          </span>
        );
      }
    },
    {
      label: localization?.ContactPersons || "Contact Persons",
      command: () => setActiveIndex(2),
      template: (item: any, options: any) => {
        return (
          <span className={options.className} onClick={options.onClick} style={{ cursor: 'pointer' }}>
            <span className="p-steps-number">
              <FontAwesomeIcon icon={faAddressBook} />
            </span>
            <span className="p-steps-title">{item.label}</span>
          </span>
        );
      }
    },
    {
      label: localization?.ClientSettings || "Client Settings",
      command: () => setActiveIndex(3),
      template: (item: any, options: any) => {
        return (
          <span className={options.className} onClick={options.onClick} style={{ cursor: 'pointer' }}>
            <span className="p-steps-number">
              <FontAwesomeIcon icon={faCog} />
            </span>
            <span className="p-steps-title">{item.label}</span>
          </span>
        );
      }
    },
    {
      label: localization?.Documents || "Documents",
      command: () => setActiveIndex(4),
      template: (item: any, options: any) => {
        return (
          <span className={options.className} onClick={options.onClick} style={{ cursor: 'pointer' }}>
            <span className="p-steps-number">
              <FontAwesomeIcon icon={faFileAlt} />
            </span>
            <span className="p-steps-title">{item.label}</span>
          </span>
        );
      }
    }
  ];

  useEffect(() => {
    const fetchInitialData = async () => {
      try {

        // Fetch countries
        const countriesResponse = await getAllCountries(dispatch, {
          Page: 1,
          Limit: 1000,
        });
        if (countriesResponse) {
          setCountries(
            countriesResponse.List.map((country: any) => ({
              label: country.country_name,
              value: country.country_id,
            }))
          );
        }

        // Fetch industries
        const industriesResponse = await getAllIndustries(dispatch, {
          Page: 1,
          Limit: 100,
        });
        if (industriesResponse) {
          setIndustries(
            industriesResponse.List.map((industry: any) => ({
              label: industry.industry_name,
              value: industry.industry_id,
            }))
          );
        }

        // Fetch client data if in edit mode
        if (mode === "edit" && id) {
          const clientResponse = await getClientById(dispatch, id);
          if (clientResponse) {
            setClient(clientResponse);

            // Check if addresses are the same
            const billing = clientResponse.billing_address;
            const shipping = clientResponse.shipping_address;

            if (billing && shipping) {
              const addressesMatch =
                billing.address_line1 === shipping.address_line1 &&
                billing.address_line2 === shipping.address_line2 &&
                billing.city === shipping.city &&
                billing.state === shipping.state &&
                billing.postal_code === shipping.postal_code &&
                billing.country_id === shipping.country_id;

              setSameAsBilling(addressesMatch);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.current?.show({
          severity: "error",
          summary: localization?.Error || "Error",
          detail: localization?.FailedToLoadData || "Failed to load data",
        });
      } 
    };

    fetchInitialData();
  }, [id, mode, dispatch]);

  // Handle client data changes
  const handleChange = (field: string, value: any) => {
    setClient((prev) => {
      const updated = { ...prev };
      const fieldParts = field.split(".");

      if (fieldParts.length === 1) {
        // Simple field
        (updated as any)[field] = value;
      } else if (fieldParts.length === 2) {
        // Nested field (billing_address or shipping_address)
        const [parent, child] = fieldParts;
        if (!(updated as any)[parent]) {
          (updated as any)[parent] = {};
        }

        ((updated as any)[parent] as any)[child] = value;
      }

      return updated;
    });

    // Clear error for the field if it exists
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle address change
  const handleAddressChange = (
    type: "billing" | "shipping",
    field: string,
    value: any
  ) => {
    const addressKey = `${type}_address`;

    setClient((prev) => {
      const updated = { ...prev };
      if (!(updated as any)[addressKey]) {
        (updated as any)[addressKey] = {} as ClientAddressDto;
      }

      const address = (updated as any)[addressKey] as ClientAddressDto;
      (address as any)[field] = value;

      // If "same as billing" is checked, update shipping address too
      if (type === "billing" && sameAsBilling) {
        if (!(updated as any).shipping_address) {
          (updated as any).shipping_address = {} as ClientAddressDto;
        }
        ((updated as any).shipping_address as any)[field] = value;
      }

      return updated;
    });
    // Clear error if valid
    if (field === "city" && value.trim()) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[`${addressKey}.city`];
        return newErrors;
      });
    }
  };

  // Toggle "same as billing" checkbox
  const handleSameAsBillingChange = (checked: boolean) => {
    setSameAsBilling(checked);

    if (checked) {
      // Copy billing address to shipping address
      setClient((prev) => ({
        ...prev,
        shipping_address: { ...(prev.billing_address || {}) },
      }));
    }
  };

  // Add a new contact person
  const addContactPerson = () => {
    setClient((prev) => ({
      ...prev,
      contact_persons: [
        ...(prev.contact_persons || []),
        { first_name: "", last_name: "", email: "", phone: "" },
      ],
    }));
  };

  // Update contact person information
  const updateContactPerson = (index: number, field: string, value: string) => {
    setClient((prev) => {
      const updatedContactPersons = [...(prev.contact_persons || [])];
      if (!updatedContactPersons[index]) {
        updatedContactPersons[index] = {
          first_name: "",
          last_name: "",
          email: "",
          phone: "",
        };
      }

      updatedContactPersons[index] = {
        ...updatedContactPersons[index],
        [field]: value,
      };

      return {
        ...prev,
        contact_persons: updatedContactPersons,
      };
    });
  };

  // Remove a contact person
  const removeContactPerson = (index: number) => {
    setClient((prev) => {
      const updatedContactPersons = [...(prev.contact_persons || [])];
      updatedContactPersons.splice(index, 1);

      return {
        ...prev,
        contact_persons: updatedContactPersons,
      };
    });
  };

  const validateFile = (file: File): { isValid: boolean; error?: string } => {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return {
        isValid: false,
        error: `${localization?.File || "File"} "${file.name}" ${localization?.MaximumFileSize || "is too large. Maximum size allowed is"} ${MAX_FILE_SIZE / (1024 * 1024)}MB.`,
      };
    }

    // Get file extension
    const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();

    // Check if extension is allowed
    if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
      return {
        isValid: false,
        error: `${localization?.File || "File"} "${file.name}" ${localization?.SupportedFormats || "has an unsupported format. Only Excel, PDF, Word, and PowerPoint files are allowed."}`,
      };
    }

    // Additional MIME type validation for better security
    const allowedMimeTypes = Object.keys(ALLOWED_FILE_TYPES);
    const isValidMimeType =
      allowedMimeTypes.includes(file.type) ||
      // Fallback for files with empty or incorrect MIME types
      ALLOWED_EXTENSIONS.includes(fileExtension);

    if (!isValidMimeType && file.type !== "") {
      return {
        isValid: false,
        error: `${localization?.File || "File"} "${file.name}" ${localization?.FileUploadError || "has an invalid file type. Only Excel, PDF, Word, and PowerPoint files are allowed."}`,
      };
    }

    return { isValid: true };
  };

  const downloadDocument = (doc: InvoiceDocumentDto, index: number) => {
    try {
      if (!doc.base64Content) {
        dispatch(
          showToast({
            detail: localization?.DocumentNotAvailable || "Document content not available for download",
            severity: "error",
            summary: localization?.Error || "Error",
          })
        );
        return;
      }

      // Convert base64 to blob
      const byteCharacters = atob(doc.base64Content);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: doc.file_type || "application/octet-stream",
      });

      // Create download URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = doc.file_name || `document_${index + 1}`;

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL
      window.URL.revokeObjectURL(url);

      dispatch(
        showToast({
          detail: `${doc.file_name} ${localization?.FileUploadedSuccessfully || "downloaded successfully"}`,
          severity: "success",
          summary: localization?.Success || "Success",
        })
      );
    } catch (error) {
      console.error("Error downloading document:", error);
      dispatch(
        showToast({
          detail: localization?.FailedToDownloadDoc || "Failed to download document",
          severity: "error",
          summary: localization?.Error || "Error",
        })
      );
    }
  };

  // Convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Get the base64 string by removing the data URL prefix
        const base64String = reader.result as string;
        // Remove the prefix (e.g., "data:application/pdf;base64,")
        const base64Content = base64String.split(",")[1];
        resolve(base64Content);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const onFileUpload = async (event: any) => {
    const files = event.files;
    setFileUploadError(""); // Clear previous errors

    // Validate each file
    const validationResults = files.map((file: File) => ({
      file,
      validation: validateFile(file),
    }));

    // Check if any files failed validation
    const invalidFiles = validationResults.filter(
      (result: any) => !result.validation.isValid
    );

    if (invalidFiles.length > 0) {
      const errorMessages = invalidFiles
        .map((result: any) => result.validation.error)
        .join("\n");
      setFileUploadError(errorMessages);
      fileUploadRef.current?.clear();
      return;
    }

    try {
      // Process only valid files
      const validFiles = validationResults.map((result: any) => result.file);
      const newUploadedFiles = client.documents ? [...client?.documents] : [];

      for (let file of validFiles) {
        const filePath = `/uploads/${Date.now()}_${file.name}`;
        const base64Content = await fileToBase64(file);
        newUploadedFiles.push({
          file_name: file.name,
          file_path: "",
          file_size: file.size,
          file_type: file.type,
          base64Content: base64Content
        });

        // Add to client documents
        setClient((prev) => ({
          ...prev,
          documents: [...(prev.documents || []), { file_path: filePath }],
        }));
      }

      setClient((prev: GetClientDto) => {
        return {
          ...prev,
          documents: newUploadedFiles,
        };
      });
      fileUploadRef.current?.clear();

      toast.current?.show({
        severity: "success",
        summary: localization?.Success || "Success",
        detail: `${validFiles.length} ${localization?.FileUploadedSuccessfully || "file(s) uploaded successfully"}`,
      });
    } catch (error) {
      console.error("Error processing files:", error);
      setFileUploadError(
        localization?.FileUploadError || "An error occurred while processing the files. Please try again."
      );
      fileUploadRef.current?.clear();
    }
  };

  // Remove an uploaded file
  const removeFile = (index: number) => {
    const newUploadedFiles = [...client?.documents];
    const removedFile = client?.documents.splice(index, 1)[0];

    setClient((prev) => ({
      ...prev,
      documents: newUploadedFiles,
    }));

    setClient((prev) => ({
      ...prev,
      documents: (prev.documents || []).filter(
        (doc: ClientDocumentDto) => doc.file_path !== removedFile.file_path
      ),
    }));
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {

        // Fetch payment terms
        const paymentTermsResponse = await getAllPaymentTerms(dispatch);
        if (paymentTermsResponse) {
          setPaymentTerms(
            paymentTermsResponse.map((term: any) => ({
              label: term.name,
              value: term.payment_term_id,
            }))
          );
        }

        // Fetch business types
        const businessTypesResponse = await getAllBusinessTypes(dispatch);
        if (businessTypesResponse) {
          setBusinessTypes(
            businessTypesResponse.map((type: any) => ({
              label: type.name,
              value: type.business_type_id,
              description: type.description
            }))
          );
        }
        // Fetch industry types
        const industryTypesResponse = await getAllIndustries(dispatch, {
          Page: 1,
          Limit: 100,
          SortBy: "ASC",
          SortProperty: "industry_name",
        });
        if (industryTypesResponse) {
          setIndustryTypes(
            industryTypesResponse?.List?.map((type: any) => ({
              label: type.industry_name,
              value: type.industry_id,
            }))
          );
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.current?.show({
          severity: "error",
          summary: localization?.Error || "Error",
          detail: localization?.FailedToLoadData || "Failed to load data",
        });
      }
    };

    fetchInitialData();
  }, [id, mode, dispatch]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!client.client_name?.trim()) {
      newErrors.client_name = localization?.ClientNameIsRequired || "Client name is required";
    }

    if (!client.primary_contact_person?.trim()) {
      newErrors.primary_contact_person = localization?.PrimaryContactPersonRequired || "Primary contact person is required";
    }

    if (!client.email?.trim()) {
      newErrors.email = localization?.EmailRequired || "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(client.email)) {
      newErrors.email = localization?.PleaseEnterValidEmail || "Please enter a valid email address";
    }

    // Phone number validation
    if (!client.phone?.trim()) {
      newErrors.phone = localization?.PhoneNumberRequired || "Phone number is required";
    } else if (!/^[0-9]{10}$/.test(client.phone)) {
      newErrors.phone = localization?.PleaseEnterValid10DigitPhone || "Please enter a valid 10-digit phone number";
    }

    // Validate contact persons
    if (client.contact_persons && client.contact_persons.length > 0) {
      client.contact_persons.forEach((person, index) => {
        if (!person.email) {
          newErrors[`contact_person_${index}_email`] =
            localization?.EmailRequired || "Email is required for contact person";
        } else if (!/^\S+@\S+\.\S+$/.test(person.email)) {
          newErrors[`contact_person_${index}_email`] =
            localization?.PleaseEnterValidEmail || "Please enter a valid email address";
        }

        if (!person.first_name) {
          newErrors[`contact_person_${index}_firstName`] =
            localization?.FirstName || "First name is required for contact person";
        }

        if (!person.last_name) {
          newErrors[`contact_person_${index}_lastName`] =
            localization?.LastName || "Last name is required for contact person";
        }
      });
    }

    // Validate billing address
    if (!client.billing_address?.city?.trim()) {
      newErrors["billing_address.city"] =
        localization?.City || "City is required for billing address";
    }

    // Validate payment term
    if (!client.payment_term_id) {
      newErrors.payment_term_id = localization?.PaymentTerms || "Payment term is required";
    }

    // Validate business type
    if (!client.business_type_id) {
      newErrors.business_type_id = localization?.BusinessTypeRequired || "Business type is required";
    }
    // Validate business type
    if (!client.currency_code) {
      newErrors.currency_code = localization?.CurrencyRequired || "Currency is required";
    }

    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      // Create a readable message from specific field errors
      const errorFields = Object.keys(validationErrors)
        .map((key) => {
          switch (key) {
            case 'payment_term_id':
              return localization?.PaymentTerms || 'Payment term';
            case 'billing_address.city':
              return localization?.City || 'Billing address (city)';
            case 'business_type_id':
              return localization?.BusinessTypeRequired || 'Business type';
            case 'currency_code':
              return localization?.CurrencyRequired || 'Currency';
            case 'client_name':
              return localization?.ClientNameIsRequired || 'Client name';
            case 'primary_contact_person':
              return localization?.PrimaryContactPersonRequired || 'Primary contact person';
            case 'email':
              return localization?.EmailRequired || 'Email';
            case 'phone':
              return localization?.PhoneNumberRequired || 'Phone number';
            default:
              if (key.startsWith("contact_person_")) return null; // Skip individual contact person fields
              return null;
          }
        })
        .filter(Boolean); // Remove nulls

      const detailMessage =
        errorFields.length > 0
          ? `${localization?.PleaseFixFollowingFields || 'Please fix the following fields'}: ${errorFields.join(', ')}`
          : localization?.PleaseFillinAllRequiredFields || 'Please fix the errors in the form';

      toast.current?.show({
        severity: 'error',
        summary: localization?.Error || 'Error',
        detail: detailMessage,
      });

      return;
    }

    setSubmitting(true);
    try {
      let response;
      let clientPayload: GetClientDto = client;

      if (mode === "edit" && id) {
        response = await updateClient(dispatch, id, clientPayload);
        if (response) {
          toast.current?.show({
            severity: "success",
            summary: localization?.Success || "Success",
            detail: localization?.ClientUpdatedSuccessfully || "Client updated successfully",
          });
        }
      } else {
        response = await createClient(dispatch, clientPayload);
        if (response) {
          toast.current?.show({
            severity: "success",
            summary: localization?.Success || "Success",
            detail: localization?.ClientCreatedSuccessfully || "Client created successfully",
          });
        } else {
          return toast.current?.show({
            severity: "error",
            summary: localization?.Success || "Error",
            detail: error || "Sorry, unable to create user",
            life: 5000
          });
        }
      }

      // Navigate back to clients list
      navigate("/client");
    } catch (error) {
      console.error("Error saving client:", error);
      toast.current?.show({
        severity: "error",
        summary: localization?.Error || "Error",
        detail: mode === "edit" ?
          (localization?.FailedToUpdateClient || "Failed to update client") :
          (localization?.FailedToCreateClient || "Failed to create client"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const checkPanNumberValidity = () => {
    if (client.pan_no?.trim() && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(client.pan_no)) {
      setErrors((prev) => ({
        ...prev,
        pan_no: 'Please enter a valid PAN number (e.g., ABCDE1234F)',
      }));
    } else {
      setErrors((prev) => ({ ...prev, pan_no: '' }));
    }
  };

  // Debounce effect when PAN number changes
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      checkPanNumberValidity();
    }, 500); // 500ms delay

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [client.pan_no]);

  const handleNext = () => {
    if (activeIndex < steps.length - 1) {
      setActiveIndex(prevIndex => prevIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (activeIndex > 0) {
      setActiveIndex(prevIndex => prevIndex - 1);
    }
  };

  // Function to render content based on the active step
  const renderStepContent = () => {
    switch (activeIndex) {
      case 0:
        return (
          // General Information
          <Card className="shadow-sm border-t-0 border-r-0 border-b-0 border-l-4 border-primary-500 p-3 card_content_padding">
            <div className="grid md:grid-rows-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {localization?.ClientName || "Client Name *"}
                </label>
                <InputText
                  value={client.client_name || ""}
                  onChange={(e) =>
                    handleChange("client_name", e.target.value)
                  }
                  className={`w-full ${errors.client_name ? "p-invalid" : ""}`}
                />
                {errors.client_name && (
                  <small className="p-error">{errors.client_name}</small>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {localization?.PrimaryContactPerson || "Primary Contact Person *"}
                </label>
                <InputText
                  value={client.primary_contact_person || ""}
                  onChange={(e) =>
                    handleChange("primary_contact_person", e.target.value)
                  }
                  className="w-full"
                />
                {errors.primary_contact_person && (
                  <small className="p-error">{errors.primary_contact_person}</small>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {localization?.Email || "Email *"}
                </label>
                <InputText
                  value={client.email || ""}
                  onChange={(e) => handleChange("email", e.target.value)}
                  className={`w-full ${errors.email ? "p-invalid" : ""}`}
                />
                {errors.email && (
                  <small className="p-error">{errors.email}</small>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {localization?.Phone || "Phone *"}
                </label>
                <InputText
                  value={client.phone || ""}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  className="w-full"
                />
                {errors.phone && (
                  <small className="p-error">{errors.phone}</small>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {localization?.PANTaxNumber || "PAN/Tax Number"}
                </label>
                <InputText
                  value={client.pan_no || ""}
                  onChange={(e) => handleChange("pan_no", e.target.value)}
                  className="w-full"
                />
                {errors.pan_no && (
                  <small className="p-error">{errors.pan_no}</small>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {localization?.WebsiteUrl || "Website Url"}
                </label>
                <InputText
                  value={client.website_url || ""}
                  onChange={(e) =>
                    handleChange("website_url", e.target.value)
                  }
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {localization?.Currency || "Currency *"}
                </label>
                <Dropdown
                  value={client.currency_code}
                  options={[
                    { label: "USD - US Dollar", value: "USD" },
                    { label: "EUR - Euro", value: "EUR" },
                    { label: "GBP - British Pound", value: "GBP" },
                    { label: "INR - Indian Rupee", value: "INR" },
                  ]}
                  onChange={(e) => handleChange("currency_code", e.value)}
                  placeholder={localization?.SelectCurrency || "Select Currency"}
                  className="w-full"
                />
                {errors.currency_code && (
                  <small className="p-error">{errors.currency_code}</small>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {localization?.BusinessType || "Business Type *"}
                </label>
                <Dropdown
                  value={client.business_type_id}
                  options={businessTypes}
                  onChange={(e) => handleChange("business_type_id", e.value)}
                  placeholder={localization?.SelectBusinessType || "Select Business Type"}
                  className={`w-full ${errors.business_type_id ? "p-invalid" : ""}`}
                  itemTemplate={(option: any) => (
                    <div className="flex flex-col">
                      <span className="font-semibold text-sm">{option.label}</span>
                      <span className="text-xs text-gray-500 whitespace-normal break-words w-full">
                        {option.description}
                      </span>
                    </div>
                  )}
                  valueTemplate={(option: any) =>
                    option ? (
                      <div className="flex flex-col">
                        <span className="font-semibold text-sm">{option.label}</span>
                        <span className="text-xs text-gray-500 whitespace-normal break-words w-full">
                          {option.description}
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-400">
                        {localization?.SelectBusinessType || "Select Business Type"}
                      </span>
                    )
                  }
                  panelClassName="w-[250px]" // 👈 controls the dropdown list width
                />
                {errors.business_type_id && (
                  <small className="p-error">{errors.business_type_id}</small>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {localization?.IndustryType || "Industry Type"}
                </label>
                <Dropdown
                  value={client.industry_id}
                  options={industryTypes}
                  onChange={(e) => handleChange("industry_id", e.value)}
                  placeholder={localization?.SelectIndustryType || "Select Industry Type"}
                  className={`w-full ${errors.industry_id ? "p-invalid" : ""}`}
                />
                {errors.industry_id && (
                  <small className="p-error">{errors.industry_id}</small>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {localization?.Notes || "Notes"}
                </label>
                <InputTextarea
                  value={client.notes || ""}
                  onChange={(e) => handleChange("notes", e.target.value)}
                  rows={3}
                  className="w-full"
                />
              </div>
            </div>
          </Card>
        );
      case 1:
        return (
          // Addresses
          <div className="space-y-3">
            <Card
              title={localization?.BillingAddress || "Billing Address"}
              className="shadow-sm border-t-0 border-r-0 border-b-0 border-l-4 border-primary-500 p-3 p_content_top_0 card_content_padding"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {localization?.AddressLine1 || "Address Line 1"}
                  </label>
                  <InputText
                    value={client.billing_address?.address_line1 || ""}
                    onChange={(e) =>
                      handleAddressChange(
                        "billing",
                        "address_line1",
                        e.target.value
                      )
                    }
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {localization?.AddressLine2 || "Address Line 2"}
                  </label>
                  <InputText
                    value={client.billing_address?.address_line2 || ""}
                    onChange={(e) =>
                      handleAddressChange(
                        "billing",
                        "address_line2",
                        e.target.value
                      )
                    }
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {localization?.City || "City *"}
                  </label>
                  <InputText
                    value={client.billing_address?.city || ""}
                    onChange={(e) =>
                      handleAddressChange("billing", "city", e.target.value)
                    }
                    className={`w-full ${errors["billing_address.city"] ? "p-invalid" : ""}`}
                  />
                  {errors["billing_address.city"] && (
                    <small className="p-error">
                      {errors["billing_address.city"]}
                    </small>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {localization?.StateProvince || "State/Province"}
                  </label>
                  <InputText
                    value={client.billing_address?.state || ""}
                    onChange={(e) =>
                      handleAddressChange("billing", "state", e.target.value)
                    }
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {localization?.PostalCode || "Postal Code"}
                  </label>
                  <InputText
                    value={client.billing_address?.postal_code || ""}
                    onChange={(e) =>
                      handleAddressChange(
                        "billing",
                        "postal_code",
                        e.target.value
                      )
                    }
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {localization?.Country || "Country"}
                  </label>
                  <Dropdown
                    value={client.billing_address?.country_id}
                    options={countries}
                    onChange={(e) =>
                      handleAddressChange("billing", "country_id", e.value)
                    }
                    placeholder={localization?.SelectCountry || "Select Country"}
                    className="w-full"
                    optionLabel="label"
                    optionValue="value"
                  />
                </div>
              </div>
            </Card>

            <div className="flex items-center ml-3 mb-3">
              <Checkbox
                checked={sameAsBilling}
                onChange={(e) =>
                  handleSameAsBillingChange(e.checked || false)
                }
                className="mr-2"
              />
              <label className="text-sm font-medium text-gray-700">
                {localization?.ShippingAddressSameAsBilling || "Shipping address same as billing"}
              </label>
            </div>

            {!sameAsBilling && (
              <Card
                title={localization?.ShippingAddress || "Shipping Address"}
                className="shadow-sm border-t-0 border-r-0 border-b-0 border-l-4 border-primary-500 p-3 p_content_top_0 card_content_padding"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {localization?.AddressLine1 || "Address Line 1"}
                    </label>
                    <InputText
                      value={client.shipping_address?.address_line1 || ""}
                      onChange={(e) =>
                        handleAddressChange(
                          "shipping",
                          "address_line1",
                          e.target.value
                        )
                      }
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {localization?.AddressLine2 || "Address Line 2"}
                    </label>
                    <InputText
                      value={client.shipping_address?.address_line2 || ""}
                      onChange={(e) =>
                        handleAddressChange(
                          "shipping",
                          "address_line2",
                          e.target.value
                        )
                      }
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {localization?.City || "City"}
                    </label>
                    <InputText
                      value={client.shipping_address?.city || ""}
                      onChange={(e) =>
                        handleAddressChange(
                          "shipping",
                          "city",
                          e.target.value
                        )
                      }
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {localization?.StateProvince || "State/Province"}
                    </label>
                    <InputText
                      value={client.shipping_address?.state || ""}
                      onChange={(e) =>
                        handleAddressChange(
                          "shipping",
                          "state",
                          e.target.value
                        )
                      }
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {localization?.PostalCode || "Postal Code"}
                    </label>
                    <InputText
                      value={client.shipping_address?.postal_code || ""}
                      onChange={(e) =>
                        handleAddressChange(
                          "shipping",
                          "postal_code",
                          e.target.value
                        )
                      }
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {localization?.Country || "Country"}
                    </label>
                    <Dropdown
                      value={client.shipping_address?.country_id}
                      options={countries}
                      onChange={(e) =>
                        handleAddressChange("shipping", "country_id", e.value)
                      }
                      placeholder={localization?.SelectCountry || "Select Country"}
                      className="w-full"
                      optionLabel="label"
                      optionValue="value"
                    />
                  </div>
                </div>
              </Card>
            )}
          </div>
        );
      case 2:
        return (
          // Contact Persons
          <Card className="shadow-sm p-3 card_content_padding">
            <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
              <h3 className="text-lg font-medium my-0">{localization?.ContactPersons || "Contact Persons"}</h3>
              <Button
                type="button"
                onClick={addContactPerson}
                className="bg-primary-600 hover:bg-primary-700"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                {localization?.AddContactPerson || "Add Contact Person"}
              </Button>
            </div>

            {client.contact_persons && client.contact_persons.length > 0 ? (
              <div className="space-y-4">
                {client.contact_persons.map((person, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-md p-3 relative"
                  >
                    <Button
                      type="button"
                      icon="pi pi-trash"
                      className="absolute top-2 right-2 p-button-danger p-button-text p-button-sm"
                      onClick={() => removeContactPerson(index)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {localization?.FirstName || "First Name *"}
                        </label>
                        <InputText
                          value={person.first_name || ""}
                          onChange={(e) =>
                            updateContactPerson(
                              index,
                              "first_name",
                              e.target.value
                            )
                          }
                          className={`w-full ${errors[`contact_person_${index}_firstName`] ? "p-invalid" : ""}`}
                        />
                        {errors[`contact_person_${index}_firstName`] && (
                          <small className="p-error">
                            {errors[`contact_person_${index}_firstName`]}
                          </small>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {localization?.LastName || "Last Name *"}
                        </label>
                        <InputText
                          value={person.last_name || ""}
                          onChange={(e) =>
                            updateContactPerson(
                              index,
                              "last_name",
                              e.target.value
                            )
                          }
                          className={`w-full ${errors[`contact_person_${index}_lastName`] ? "p-invalid" : ""}`}
                        />
                        {errors[`contact_person_${index}_lastName`] && (
                          <small className="p-error">
                            {errors[`contact_person_${index}_lastName`]}
                          </small>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {localization?.Email || "Email *"}
                        </label>
                        <InputText
                          value={person.email || ""}
                          onChange={(e) =>
                            updateContactPerson(
                              index,
                              "email",
                              e.target.value
                            )
                          }
                          className={`w-full ${errors[`contact_person_${index}_email`] ? "p-invalid" : ""}`}
                        />
                        {errors[`contact_person_${index}_email`] && (
                          <small className="p-error">
                            {errors[`contact_person_${index}_email`]}
                          </small>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {localization?.Phone || "Phone"}
                        </label>
                        <InputText
                          value={person.phone || ""}
                          onChange={(e) =>
                            updateContactPerson(
                              index,
                              "phone",
                              e.target.value
                            )
                          }
                          className="w-full"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-500 text-center py-6">
                {localization?.NoContactPersonsAdded || "No contact persons added. Click the button above to add a contact person."}
              </div>
            )}
          </Card>
        );
      case 3:
        return (
          // Client Settings
          <Card className="shadow-sm p-3 card_content_padding">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {localization?.PaymentTerms || "Payment Terms *"}
              </label>
              <Dropdown
                value={client.payment_term_id}
                options={paymentTerms}
                onChange={(e) => handleChange("payment_term_id", e.value)}
                placeholder={localization?.SelectPaymentTerms || "Select Payment Term"}
                className={`w-full ${errors.payment_term_id ? "p-invalid" : ""}`}
              />
              {errors.payment_term_id && (
                <small className="p-error">{errors.payment_term_id}</small>
              )}
            </div>
          </Card>
        );
      case 4:
        return (
          // Documents
          <Card className="shadow-sm p-3 card_content_padding">
            <div className="mb-4">
              <h3 className="text-lg font-medium mb-2 mt-0">{localization?.UploadDocuments || "Upload Documents"}</h3>
              {fileUploadError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-red-700 text-sm whitespace-pre-line">
                    {fileUploadError}
                  </p>
                </div>
              )}
              <FileUpload
                ref={fileUploadRef}
                multiple
                customUpload
                uploadHandler={onFileUpload}
                accept={Object.keys(ALLOWED_FILE_TYPES).join(",")}
                maxFileSize={MAX_FILE_SIZE}
                auto={true}  // This makes it upload immediately on file selection
                headerTemplate={(options: any) => (
                  <div className="p-fileupload-buttonbar">
                    <span className=" flex items-center gap-2 p-buttonset">
                      {options.chooseButton}
                      <Button
                        type="button" // 👈 important!
                        disabled={client?.documents?.length === 0}
                        // icon="fa-solid fa-xmark"
                        className="p-3 text-base font-medium flex items-center gap-2"
                        onClick={() => {
                          setClient({
                            ...client,
                            documents: [],
                          })
                        }}
                      >
                        <FontAwesomeIcon icon={faXmark} className="w-4 h-4 text-base mr-2" />
                        {localization?.Cancel || "Cancel"}
                      </Button>
                    </span>
                  </div>
                )}
                emptyTemplate={
                  (client.documents?.length ?? 0) > 0 ? <></> : (
                    <div className="text-center p-4">
                      <p className="m-0 mb-2">
                        {localization?.DragAndDropFilesHereToUpload || "Drag and drop files here to upload."}
                      </p>
                      <p className="text-sm text-gray-600 m-0">
                        {localization?.SupportedFormats || "Supported formats: Excel (.xlsx, .xls, .xlsm, etc.), PDF (.pdf), Word (.docx, .doc, etc.), PowerPoint (.pptx, .ppt, etc.)"}
                      </p>
                      <p className="text-xs text-gray-500 m-0 mt-1">
                        {localization?.MaximumFileSize || "Maximum file size:"} {MAX_FILE_SIZE / (1024 * 1024)}MB
                      </p>
                    </div>)
                }
                onError={() => {
                  setFileUploadError(
                    localization?.FileUploadError || "An error occurred during file upload. Please try again."
                  );
                }}
                onClear={() => {
                  setFileUploadError("");
                }}
              />
            </div>

            <div className="mt-4">
              <h3 className="text-lg font-medium mb-2">
                {localization?.UploadedDocuments || "Uploaded Documents"}{" "}
                {client?.documents?.length > 0 &&
                  `(${client?.documents?.length})`}
              </h3>
              {client?.documents && client?.documents?.length > 0 ? (
                <div className="space-y-2">
                  {client?.documents.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-md border hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex-grow">
                        <div className="flex items-center">
                          <FontAwesomeIcon
                            icon={faFile}
                            className="mr-2 text-blue-500"
                          />
                          <span className="font-medium">
                            {file.file_name}
                          </span>
                          {file.file_size && (
                            <span className="text-sm text-gray-500 ml-2">
                              ({(file.file_size / 1024).toFixed(2)} KB)
                            </span>
                          )}
                        </div>
                        {file.file_type && (
                          <span className="text-xs text-gray-400 ml-6">
                            {file.file_type}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          icon="pi pi-download"
                          className="p-button-text p-button-rounded p-button-sm hover:bg-blue-100"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            downloadDocument(file, index);
                          }}
                          children={<FontAwesomeIcon icon={faDownload} />}
                          aria-label={`${localization?.Download || "Download"} ${file.file_name}`}
                        />

                        <Button
                          type="button"
                          className="p-button-danger p-button-text p-button-sm hover:bg-red-100"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            removeFile(index);
                          }}
                          aria-label={`${localization?.Remove || "Remove"} ${file.file_name}`}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-gray-500 text-center py-4 border border-dashed border-gray-300 rounded-md">
                  {localization?.NoDocumentsUploaded || "No documents uploaded."}
                </div>
              )}
            </div>
          </Card>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <Toast ref={toast} className="z-10" />
      <h1 className="text-xl font-bold surface-900 mb-4">
        {mode === "edit" ? (localization?.EditClient || "Edit Client") : (localization?.CreateNewClient || "Create New Client")}
      </h1>

      <form className="space-y-2 bg-gray-50 darkBg p-4 rounded-md" onSubmit={handleSubmit}>
        <div className="mb-5">
          <Steps
            model={steps}
            activeIndex={activeIndex}
            onSelect={(e) => setActiveIndex(e.index)}
            readOnly={false}
            className="mb-5 "
          />
        </div>

        {renderStepContent()}

        <div className="flex justify-between mt-4 pt-3 flex-wrap gap-2 border-t border-gray-200">
          <div>
            {activeIndex > 0 && (
              <Button
                type="button"
                onClick={handlePrevious}
                className="py-1 text-sm mr-2"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-1" />
                {localization?.Previous || "Previous"}
              </Button>
            )}
          </div>

          <div className="flex space-x-2">
            <Button
              type="button"
              onClick={() => navigate("/client")}
              className="py-1 text-sm"
            >
              <FontAwesomeIcon icon={faXmark} className="mr-1" />
              {localization?.Cancel || "Cancel"}
            </Button>

            {activeIndex < steps.length - 1 ? (
              <Button
                type="button"
                onClick={handleNext}
                className="bg-primary-600 hover:bg-primary-700 py-1 text-sm"
              >
                {localization?.Next || "Next"}
                <FontAwesomeIcon icon={faArrowRight} className="ml-1" />
              </Button>
            ) : (
              <Button
                type="submit"
                isLoading={submitting}
                className="bg-primary-600 hover:bg-primary-700 py-1 text-sm"
              >
                <FontAwesomeIcon icon={faCheck} className="mr-1" />
                {mode === "edit" ?
                  (localization?.UpdateClient || "Update Client") :
                  (localization?.CreateClient || "Create Client")}
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default ClientForm;